import { Modal, Flex } from 'antd'
import { useState, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import Option from '../SelectModal/Option'
import Header from '../SelectModal/Header'
import Search from '../SelectModal/Search'
import { ICryptoCurrency, INetwork } from '../../api/types'
import { lang } from './lang'
import s from './s.module.css'
import ActionButton from './ActionButton'
import { checkNetworksAvailable, getIcon, getList } from './utils'

type Props = {
  selectedCurrency: ICryptoCurrency | null
  selectedNetwork: INetwork | null
  isOpen: boolean
  onClose: () => void
  options: ICryptoCurrency[]
  onSelectCurrency: (value: ICryptoCurrency | null) => void
  onSelectNetwork: (value: INetwork | null) => void
}

export type Step = 'coin' | 'network'

export default function CryptoSelect({
  isOpen,
  onClose,
  options,
  selectedCurrency,
  selectedNetwork,
  onSelectCurrency,
  onSelectNetwork,
}: Props) {
  const [step, setStep] = useState<'coin' | 'network'>('coin')
  const isCoinStep = step === 'coin'
  const [searchQuery, setSearchQuery] = useState('')

  const list = useMemo(
    () => getList({ options, step, selectedCurrency, searchQuery }),
    [options, searchQuery, selectedCurrency, step]
  )

  const showButton = list?.length > 0
  const selectedAssetLabel = selectedCurrency ? `${selectedCurrency.code} ${selectedCurrency.name}` : ''

  const handleClose = () => {
    onClose?.()
    setSearchQuery('')
    setStep('coin')
  }

  return (
    <Modal open={isOpen} footer={false} onCancel={handleClose} width={408}>
      <Flex vertical gap={16}>
        <Header
          showBackButton={!isCoinStep}
          onBackClick={() => setStep('coin')}
          title={
            isCoinStep ? (
              <FormattedMessage {...lang.selectCurrencyTitle} />
            ) : (
              <FormattedMessage {...lang.selectNetworkTitle} />
            )
          }
          subtitle={
            isCoinStep ? (
              <FormattedMessage {...lang.selectCurrencySubtitle} values={{ amount: options.length }} />
            ) : (
              selectedAssetLabel
            )
          }
        />
        {isCoinStep && <Search searchQuery={searchQuery} onChange={setSearchQuery} />}
        <Flex vertical className={s.list}>
          {list.map(option => {
            const { icon, badge } = getIcon({ option, step, selectedCurrency, currencies: options }) ?? {}
            const optionProps = {
              code: isCoinStep ? option.code : (option as INetwork).protocol,
              name: option.name,
              icon,
              iconBadgeSrc: badge,
              isSelected: isCoinStep ? selectedCurrency?.code === option.code : selectedNetwork?.code === option.code,
              onClick: () => {
                if (isCoinStep) {
                  onSelectCurrency(option as ICryptoCurrency)
                } else {
                  onSelectNetwork(option as INetwork)
                }
              },
            }

            return <Option key={option.code} {...optionProps} />
          })}
        </Flex>
        {showButton && (
          <ActionButton
            step={step}
            onChangeStep={() => setStep('network')}
            onClose={handleClose}
            isNetworksAvailable={checkNetworksAvailable(selectedCurrency)}
          />
        )}
      </Flex>
    </Modal>
  )
}
